import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import {
  UserMax,
  Energy,
  Mana,
  LookInfo,
  UpgradesInfo,
  CharacteristicEnum,
  StageInitData,
  RewardReference,
  Caravan,
  CaravanElement,
} from "../handlers/types";
import {
  Character,
  DEFAULT_CHARACTER_ID,
} from "../scripts/game/character/Character";
import ApiService from "../handlers/apiService";
import { StorageService } from "../services/LocalStorageService";
import posthog from "posthog-js";

interface GameState {
  // User data
  user: UserMax | null;

  // Energy data (separate for real-time updates)
  energy: Energy | null;

  // Characters and looks
  character: Character | null;
  upgradesInfo: UpgradesInfo | null;
  userLooks: LookInfo[];
  selectedLookId: string | null;

  // Stage data
  stageData: StageInitData | null;
  isStageInitialized: boolean;

  // Loading and error states
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;
  isInitializing: boolean;

  // Energy timer
  energyTimer: NodeJS.Timeout | null;
  isRefreshingEnergy: boolean;

  // Actions
  initialize: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  refreshUpgrades: () => Promise<void>;
  refreshLooks: () => Promise<void>;
  updateUser: (user: UserMax) => void;
  updateEnergy: (energy: Energy) => void;
  updateMana: (mana: Mana) => void;
  updateBalance: (balance: number) => void;
  refreshEnergyFromApi: () => Promise<void>;
  startEnergyUpdates: () => void;
  stopEnergyUpdates: () => void;
  increaseEnergy: (amount: number) => number;
  decreaseEnergy: () => number;
  refillEnergyFull: () => number;
  hasEnergy: () => boolean;
  updateCharacter: (character: Character | null) => void;
  updateUpgradesInfo: (upgradesInfo: UpgradesInfo) => void;
  updateLooks: (looks: LookInfo[]) => void;
  selectLook: (lookId: string) => Promise<boolean>;
  spendEnergy: (amount: number) => Promise<boolean>;
  upgradeCharacteristic: (
    characteristic: CharacteristicEnum
  ) => Promise<boolean>;

  // Stage actions
  initializeStage: (stageId: string) => Promise<StageInitData | null>;
  clearStageData: () => void;
  popBossId: () => RewardReference | null;
  popTreasureId: () => RewardReference | null;
  popCaravanElement: () => CaravanElement | null;
  getEndgameId: () => RewardReference | null;
  getGiftCoinsId: () => RewardReference | null;
  getCaravan: () => Caravan | null;
  getRunId: () => string | null;
  hasCaravanElements: () => boolean;
  claimReward: (id: string, value?: number) => Promise<boolean>;

  reset: () => void;
}

function ensureCharacterPersisted(
  upgradesInfo: UpgradesInfo,
  getState: () => GameState
): Character {
  const existingCharacterData = StorageService.loadCharacter();
  if (existingCharacterData) {
    return new Character(existingCharacterData);
  }

  const state = getState();
  const character = Character.createInitialCharacter(upgradesInfo, {
    id: DEFAULT_CHARACTER_ID,
    lookId: state.selectedLookId,
  });

  StorageService.saveCharacter(character);
  return character;
}

export const useGameStore = create<GameState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    user: null,
    energy: null,
    character: null,
    upgradesInfo: null,
    userLooks: [],
    selectedLookId: null,
    stageData: null,
    isStageInitialized: false,
    isInitialized: false,
    isLoading: true,
    error: null,
    isInitializing: false,
    energyTimer: null,
    isRefreshingEnergy: false,

    // Initialize the store
    initialize: async () => {
      // Prevent multiple simultaneous initializations
      const state = get();
      if (state.isInitializing || state.isInitialized) {
        return;
      }

      try {
        set({ isLoading: true, error: null, isInitializing: true });

        const api = ApiService.getInstance();

        // Initialize user first
        const user = await api.api.webappInitCreate();
        if (user) {
          StorageService.saveUser(user);

          // Identify user in PostHog
          posthog.identify(user.id.toString(), {
            firstName: user.firstName,
            secondName: user.secondName,
            username: user.username,
            lastLoginAt: user.lastLoginAt,
          });

          set({ user });

          // Update energy if available
          if ("energy" in user) {
            set({ energy: (user as any).energy });
          }
        }

        // Load character upgrades info
        try {
          const upgradesResponse = await api.api.upgradesList();
          if (upgradesResponse) {
            set({ upgradesInfo: upgradesResponse });

            // Ensure character exists and is persisted
            const character = ensureCharacterPersisted(upgradesResponse, get);
            set({ character });
          }
        } catch (error) {
          console.error("Failed to load character upgrades:", error);
        }

        // Load looks
        try {
          const looksResponse = await api.api.looksList();
          if (looksResponse?.looks) {
            set({ userLooks: looksResponse.looks });

            // Find selected look
            const selectedLook = looksResponse.looks.find(
              (look) => look.isSelected
            );
            if (selectedLook) {
              set({ selectedLookId: selectedLook.id });
            }
          }
        } catch (error) {
          console.error("Failed to load looks:", error);
        }

        // Start energy updates
        get().startEnergyUpdates();

        set({ isInitialized: true, isLoading: false, isInitializing: false });
      } catch (err) {
        console.error("Failed to initialize store:", err);
        set({
          error: err instanceof Error ? err.message : "Failed to initialize",
          isLoading: false,
          isInitializing: false,
        });
      }
    },

    // Refresh only user data (balance, energy, mana)
    refreshUserData: async () => {
      try {
        const api = ApiService.getInstance();
        const user = await api.api.usersList();
        if (user) {
          StorageService.saveUser(user);
          set({ user });

          // Update energy if available
          if ("energy" in user) {
            set({ energy: (user as any).energy });
          }
        }
      } catch (error) {
        console.error("Failed to refresh user data:", error);
      }
    },

    // Refresh only upgrades data
    refreshUpgrades: async () => {
      try {
        const api = ApiService.getInstance();
        const upgradesResponse = await api.api.upgradesList();
        if (upgradesResponse) {
          set({ upgradesInfo: upgradesResponse });

          // Ensure character exists and is persisted after refresh
          const character = ensureCharacterPersisted(upgradesResponse, get);
          set({ character });
        }
      } catch (error) {
        console.error("Failed to refresh upgrades:", error);
      }
    },

    // Refresh only looks data
    refreshLooks: async () => {
      try {
        const api = ApiService.getInstance();
        const looksResponse = await api.api.looksList();
        if (looksResponse?.looks) {
          set({ userLooks: looksResponse.looks });

          // Find selected look
          const selectedLook = looksResponse.looks.find(
            (look) => look.isSelected
          );
          if (selectedLook) {
            set({ selectedLookId: selectedLook.id });
          }
        }
      } catch (error) {
        console.error("Failed to refresh looks:", error);
      }
    },

    // Update user data
    updateUser: (user) => {
      StorageService.saveUser(user);
      set({ user });

      // Update energy if included
      if ("energy" in user) {
        set({ energy: (user as any).energy });
        get().startEnergyUpdates();
      }
    },

    // Update energy
    updateEnergy: (energy) => {
      set({ energy });
      const currentUser = get().user;
      if (currentUser) {
        const updatedUser = { ...currentUser, energy } as any;
        StorageService.saveUser(updatedUser);
      }
    },

    // Update mana
    updateMana: (mana) => {
      set((state) => {
        if (!state.user) return state;
        const updatedUser = { ...state.user, mana };
        StorageService.saveUser(updatedUser);
        return { user: updatedUser };
      });
    },

    // Update balance
    updateBalance: (balance) => {
      set((state) => {
        if (!state.user) return state;
        const updatedUser = { ...state.user, balance };
        StorageService.saveUser(updatedUser);
        return { user: updatedUser };
      });
    },

    // Refresh energy from API
    refreshEnergyFromApi: async () => {
      const state = get();
      if (state.isRefreshingEnergy || !state.user) {
        return;
      }

      set({ isRefreshingEnergy: true });

      try {
        const api = ApiService.getInstance();
        // Energy data comes from usersList endpoint
        const response = await api.api.usersList();

        if (response?.energy) {
          set({ energy: response.energy, isRefreshingEnergy: false });

          // Update stored user with new energy
          const currentUser = state.user;
          if (currentUser) {
            const updatedUser = {
              ...currentUser,
              energy: response.energy,
            } as any;
            StorageService.saveUser(updatedUser);
          }
        }
      } catch (error) {
        console.error("Failed to refresh energy from API:", error);
        set({ isRefreshingEnergy: false });
      }
    },

    // Start energy update timer
    startEnergyUpdates: () => {
      const state = get();

      // Clear existing timer
      if (state.energyTimer) {
        clearTimeout(state.energyTimer);
        set({ energyTimer: null });
      }

      const energy = state.energy;
      if (!energy) {
        // If no energy data, fetch from API first
        state.refreshEnergyFromApi().then(() => {
          state.startEnergyUpdates();
        });
        return;
      }

      // If energy is full, no need for timer
      if (energy.current >= energy.max) {
        return;
      }

      const nextRestoreMs = energy.nextRestore;
      if (!nextRestoreMs) return;

      const timeUntilNextRestore = Math.max(100, nextRestoreMs);

      const timer = setTimeout(() => {
        state.refreshEnergyFromApi().then(() => {
          state.startEnergyUpdates();
        });
      }, timeUntilNextRestore);

      set({ energyTimer: timer });
    },

    // Stop energy updates
    stopEnergyUpdates: () => {
      const timer = get().energyTimer;
      if (timer) {
        clearTimeout(timer);
        set({ energyTimer: null });
      }
    },

    // Increase energy by specific amount
    increaseEnergy: (amount: number) => {
      const state = get();
      const currentEnergy = state.energy;

      if (!currentEnergy) {
        return 0;
      }

      // Update local state immediately for responsiveness
      const newEnergy = Math.min(
        currentEnergy.max,
        currentEnergy.current + amount
      );
      const updatedEnergyData = { ...currentEnergy, current: newEnergy };

      set({ energy: updatedEnergyData });

      // Update stored user with new energy
      const currentUser = state.user;
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          energy: updatedEnergyData,
        } as any;
        StorageService.saveUser(updatedUser);
      }

      // Refresh from API to sync the server state
      setTimeout(() => {
        state.refreshEnergyFromApi().then(() => {
          state.startEnergyUpdates();
        });
      }, 100);

      return newEnergy;
    },

    // Decrease energy by 1
    decreaseEnergy: () => {
      const state = get();
      const currentEnergy = state.energy;

      if (!currentEnergy) {
        return 0;
      }

      // Update local state immediately for responsiveness
      const newEnergy = Math.max(0, currentEnergy.current - 1);
      const updatedEnergyData = { ...currentEnergy, current: newEnergy };

      set({ energy: updatedEnergyData });

      // Update stored user with new energy
      const currentUser = state.user;
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          energy: updatedEnergyData,
        } as any;
        StorageService.saveUser(updatedUser);
      }

      // Refresh from API to sync the server state
      setTimeout(() => {
        state.refreshEnergyFromApi().then(() => {
          state.startEnergyUpdates();
        });
      }, 100);

      return newEnergy;
    },

    // Refill energy to maximum
    refillEnergyFull: () => {
      const state = get();
      const currentEnergy = state.energy;

      if (!currentEnergy) {
        return 0;
      }

      // Update local state immediately for responsiveness
      const updatedEnergyData = {
        ...currentEnergy,
        current: currentEnergy.max,
      };

      set({ energy: updatedEnergyData });

      // Update stored user with new energy
      const currentUser = state.user;
      if (currentUser) {
        const updatedUser = {
          ...currentUser,
          energy: updatedEnergyData,
        } as any;
        StorageService.saveUser(updatedUser);
      }

      // Refresh from API to sync the server state
      setTimeout(() => {
        state.refreshEnergyFromApi().then(() => {
          state.startEnergyUpdates();
        });
      }, 100);

      return currentEnergy.max;
    },

    // Check if user has energy to play
    hasEnergy: () => {
      const energy = get().energy;
      return (energy?.current || 0) > 0;
    },

    // Update character
    updateCharacter: (character) => {
      set({ character });
      if (character) {
        StorageService.saveCharacter(character);
      }
    },

    // Update upgrades info
    updateUpgradesInfo: (upgradesInfo) => {
      set({ upgradesInfo });
    },

    // Update looks
    updateLooks: (looks) => {
      set({ userLooks: looks });
    },

    // Select a look
    selectLook: async (lookId) => {
      try {
        const api = ApiService.getInstance();
        const response = await api.api.looksSelectCreate(lookId);

        if (response?.success) {
          set({ selectedLookId: lookId });

          // Update looks to reflect selection
          set((state) => ({
            userLooks: state.userLooks.map((look) => ({
              ...look,
              isSelected: look.id === lookId,
            })),
          }));

          // Refresh character upgrades with new look
          await get().refreshUpgrades();

          return true;
        }
        return false;
      } catch (error) {
        console.error("Failed to select look:", error);
        return false;
      }
    },

    // Upgrade characteristic
    upgradeCharacteristic: async (characteristic) => {
      try {
        const api = ApiService.getInstance();
        const response = await api.api.upgradesCreate({
          characteristic,
        });

        if (response) {
          // Refresh upgrades and user data
          await get().refreshUpgrades();
          await get().refreshUserData();

          return true;
        }
        return false;
      } catch (error) {
        console.error("Failed to upgrade characteristic:", error);
        return false;
      }
    },

    // Spend energy - Energy is automatically spent when starting a stage
    // This method is kept for compatibility but energy spending happens server-side
    spendEnergy: async (amount) => {
      const state = get();
      const currentEnergy = state.energy?.current || 0;

      if (currentEnergy < amount) {
        console.warn(
          `Not enough energy. Required: ${amount}, Available: ${currentEnergy}`
        );
        return false;
      }

      // Energy is spent server-side when starting a stage
      // Just refresh to get the updated energy
      await state.refreshEnergyFromApi();
      return true;
    },

    // Initialize stage data
    initializeStage: async (stageId: string) => {
      try {
        const api = ApiService.getInstance();
        const stageData = await api.api.stagesInitList(stageId);
        set({ stageData, isStageInitialized: true });
        console.log("Stage data initialized:", stageData);
        return stageData;
      } catch (error) {
        console.error("Failed to initialize stage data:", error);
        set({ stageData: null, isStageInitialized: false });
        return null;
      }
    },

    // Clear stage data
    clearStageData: () => {
      set({ stageData: null, isStageInitialized: false });
    },

    // Pop boss ID (mutates bosses array)
    popBossId: () => {
      const state = get();
      const bossId = state.stageData?.bosses?.shift() || null;
      // Trigger state update to notify subscribers
      if (bossId && state.stageData) {
        set({ stageData: { ...state.stageData } });
      }
      return bossId;
    },

    // Pop treasure ID (mutates treasures array)
    popTreasureId: () => {
      const state = get();
      const treasureId = state.stageData?.treasures?.shift() || null;
      // Trigger state update to notify subscribers
      if (treasureId && state.stageData) {
        set({ stageData: { ...state.stageData } });
      }
      return treasureId;
    },

    // Pop caravan element (mutates caravan pool)
    popCaravanElement: () => {
      const state = get();
      const element = state.stageData?.caravan?.pool.shift() || null;
      // Trigger state update to notify subscribers
      if (element && state.stageData) {
        set({ stageData: { ...state.stageData } });
      }
      return element;
    },

    // Get endgame ID
    getEndgameId: () => {
      return get().stageData?.endgame || null;
    },

    // Get gift coins ID
    getGiftCoinsId: () => {
      return get().stageData?.giftCoins || null;
    },

    // Get caravan data
    getCaravan: () => {
      return get().stageData?.caravan || null;
    },

    // Get run ID
    getRunId: () => {
      return get().stageData?.runId || null;
    },

    // Check if caravan has elements
    hasCaravanElements: () => {
      const caravan = get().stageData?.caravan;
      return !!(caravan?.pool && caravan.pool.length > 0);
    },

    // Claim reward
    claimReward: async (id: string, value?: number) => {
      try {
        const api = ApiService.getInstance();
        const response = await api.api.rewardsClaimCreate({ id, value });
        return response.success;
      } catch (error) {
        console.error("Failed to claim reward:", error);
        return false;
      }
    },

    // Reset store
    reset: () => {
      get().stopEnergyUpdates();
      set({
        user: null,
        energy: null,
        character: null,
        upgradesInfo: null,
        userLooks: [],
        selectedLookId: null,
        stageData: null,
        isStageInitialized: false,
        isInitialized: false,
        isLoading: false,
        error: null,
        isInitializing: false,
        energyTimer: null,
        isRefreshingEnergy: false,
      });
    },
  }))
);

// Selectors for common data access patterns
export const selectUser = (state: GameState) => state.user;
export const selectBalance = (state: GameState) => state.user?.balance || 0;
export const selectMana = (state: GameState) => state.user?.mana || null;
export const selectEnergy = (state: GameState) => state.energy?.current || 0;
export const selectMaxEnergy = (state: GameState) => state.energy?.max || 0;
export const selectEnergyData = (state: GameState) => state.energy;
export const selectCharacter = (state: GameState) => state.character;
export const selectUpgradesInfo = (state: GameState) => state.upgradesInfo;
export const selectLooks = (state: GameState) => state.userLooks;
export const selectSelectedLookId = (state: GameState) => state.selectedLookId;
export const selectIsInitialized = (state: GameState) => state.isInitialized;
export const selectIsLoading = (state: GameState) => state.isLoading;
export const selectError = (state: GameState) => state.error;

// Stage selectors
export const selectStageData = (state: GameState) => state.stageData;
export const selectIsStageInitialized = (state: GameState) =>
  state.isStageInitialized;
export const selectRunId = (state: GameState) => state.stageData?.runId;
export const selectEndgame = (state: GameState) => state.stageData?.endgame;
export const selectGiftCoins = (state: GameState) => state.stageData?.giftCoins;
export const selectCaravan = (state: GameState) => state.stageData?.caravan;
export const selectBossesCount = (state: GameState) =>
  state.stageData?.bosses?.length || 0;
export const selectTreasuresCount = (state: GameState) =>
  state.stageData?.treasures?.length || 0;
export const selectCaravanElementsCount = (state: GameState) =>
  state.stageData?.caravan?.pool.length || 0;
